import React, { useEffect, useState } from 'react';
import styles from '../internal_transfer/style.module.scss';
import classNames from 'classnames';
import DashboardWrapper from 'shared/components/common/dashboard_wrapper/DashboardWrapper';
import Heading from 'shared/components/common/heading/Heading';
import CustomButton from 'shared/components/common/custom_button/CustomButton';
import Select from 'react-select';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { routeConstant } from 'shared/routes/RouteConstant';
import { toastMessage } from 'shared/components/toast/Toast';
import OverlayLoading from 'shared/screen_loader/overlay_loader/OverlayLoading';
import { GetAllWarehouse } from 'shared/services/ware_house_service/WareHouseService';
import { GetSkuByWarehouseQuery } from 'shared/services/inventory_service/InventoryService';
import { AddReturnOrderMutation } from 'shared/services/return_service/ReturnService';
import { GetAllSOQuery } from 'shared/services/so_service/SOService';
import ReturnItemTable from './ReturnItemTable';
import ReturnOrderModal from 'shared/modal/return_order/ReturnOrderModal';

export default function AddReturnOrder() {
    const navigate = useNavigate();
    const { theme: { theme } } = useSelector((state) => state.root);
    const isDark = theme?.toLowerCase() === 'dark';

    const { data: listWareHouse } = GetAllWarehouse();
    const { data: listSaleOrders } = GetAllSOQuery();
    const { mutate: addReturnMutation, isLoading: returnLoading, isSuccess: returnIsSuccess } = AddReturnOrderMutation();

    const [wareHouses, setWareHouses] = useState([]);
    const [selectedWarehouse, setSelectedWarehouse] = useState(null);
    const [saleOrders, setSaleOrders] = useState([]);
    const [selectedSO, setSelectedSO] = useState(null);
    const [returnListing, setReturnListing] = useState([]);
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        if (listWareHouse) {
            const options = listWareHouse.map(w => ({
                label: w?.code,
                value: w?._id,
            }));
            setWareHouses(options);
        }
    }, [listWareHouse]);

    useEffect(() => {
        if (listSaleOrders) {
            const options = listSaleOrders.map(so => ({
                label: `SO# ${so?.order_number} (${so?.shipping?.customerName || so?.supplier?.name || 'N/A'})`,
                value: so?._id,
                fullData: so
            }));
            setSaleOrders(options);
        }
    }, [listSaleOrders]);

    useEffect(() => {
        if (returnIsSuccess) {
            navigate(routeConstant.admin.returnOrder.list.path);
        }
    }, [returnIsSuccess, navigate]);

    const handleSOSelect = (selected) => {
        setSelectedSO(selected);
        const soData = selected?.fullData;

        // Auto-select warehouse based on SO data
        if (soData) {
            const whId = soData.warehouse?._id || soData.warehouse || soData.order?.[0]?.warehouse?._id || soData.order?.[0]?.warehouse;
            if (whId) {
                const matchingWH = wareHouses.find(w => w.value === whId);
                if (matchingWH) setSelectedWarehouse(matchingWH);
            }
        }

        if (soData?.order) {
            const items = soData.order.map(item => ({
                ...item,
                maxReturnQty: item.quantity,
                returnQty: 0,
                isSelected: false
            }));
            setReturnListing(items);
        }
    };

    const handleUpdateQuantity = (index, newQty) => {
        const updatedList = [...returnListing];
        if (updatedList[index]) {
            const qty = Number(newQty);
            const max = updatedList[index].maxReturnQty;
            if (qty > max) {
                toastMessage('error', `Cannot return more than sold quantity (${max})`);
                return;
            }
            updatedList[index].returnQty = qty;
            setReturnListing(updatedList);
        }
    };

    const handleToggleSelect = (index) => {
        const updatedList = [...returnListing];
        if (updatedList[index]) {
            updatedList[index].isSelected = !updatedList[index].isSelected;
            // If selected, default returnQty to max if zero
            if (updatedList[index].isSelected && updatedList[index].returnQty === 0) {
                updatedList[index].returnQty = updatedList[index].maxReturnQty;
            }
            setReturnListing(updatedList);
        }
    };

    const handleSubmit = () => {
        if (!selectedWarehouse) return toastMessage('error', "Please select a warehouse to receive return");
        if (!selectedSO) return toastMessage('error', "Please select a Sale Order");
        const selectedItems = returnListing.filter(item => item.isSelected && item.returnQty > 0);
        if (selectedItems.length === 0) return toastMessage('error', "Please select at least one item with quantity > 0 to return");

        setShowModal(true);
    };

    const handleFinalSubmit = (modalValues) => {
        const selectedItems = returnListing.filter(item => item.isSelected && item.returnQty > 0);
        const payload = {
            warehouse: selectedWarehouse.value,
            sale_order: selectedSO.value,
            items: selectedItems.map(item => ({
                sku: item.sku,
                quantity: item.returnQty
            })),
            reason: modalValues.reason
        };

        addReturnMutation(payload);
    };

    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            borderRadius: '8px',
            border: state.isFocused ? '1px solid #4a86f7' : '1px solid #c9d1e1',
            boxShadow: state.isFocused ? '0 0 0 1px #4a86f7' : 'none',
            '&:hover': { border: '1px solid #4a86f7' },
            padding: '2px 4px',
            minHeight: '45px',
            backgroundColor: isDark ? '#262c48' : '#fff',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
        }),
        placeholder: (provided) => ({ ...provided, color: '#a8a8a8', fontSize: '14px' }),
        singleValue: (provided) => ({ ...provided, color: isDark ? '#fff' : '#000' }),
        menu: (provided) => ({
            ...provided,
            backgroundColor: isDark ? '#262c48' : '#fff',
            borderRadius: '8px',
            overflow: 'hidden',
            zIndex: 100,
            border: isDark ? '1px solid #353f5f' : '1px solid #c9d1e1',
            boxShadow: '0 4px 11px rgba(0, 0, 0, 0.15)',
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#4a86f7' : state.isFocused ? (isDark ? '#353f5f' : '#e2ecff') : 'transparent',
            color: state.isSelected ? '#fff' : (isDark ? '#fff' : '#000'),
            cursor: 'pointer',
            padding: '10px 15px',
        }),
        input: (provided) => ({ ...provided, color: isDark ? '#fff' : '#000' }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    };

    return (
        <DashboardWrapper>
            <div className={classNames(styles.UserContainer, 'text-start')}>
                <div className={styles.Header}>
                    <Heading heading={'Add Return Order'} headingStyle={styles.Heading} />
                    <div className={styles.SelectionRow}>
                        <div className={styles.SelectContainer} style={{ width: '45%' }}>
                            <label className={styles.InputLabel}>Select Sale Order</label>
                            <Select
                                options={saleOrders}
                                styles={customSelectStyles}
                                isSearchable
                                placeholder="Search SO# ..."
                                menuPortalTarget={document.body}
                                onChange={handleSOSelect}
                                value={selectedSO}
                            />
                        </div>
                        <div className={styles.SelectContainer} style={{ width: '45%' }}>
                            <label className={styles.InputLabel}>Receiving Warehouse</label>
                            <Select
                                options={wareHouses}
                                styles={customSelectStyles}
                                isSearchable
                                placeholder="Select Warehouse"
                                menuPortalTarget={document.body}
                                onChange={(selected) => setSelectedWarehouse(selected)}
                                value={selectedWarehouse}
                            />
                        </div>
                    </div>
                </div>

                <div className={classNames(styles.Body, "mt-4")}>
                    <ReturnItemTable
                        items={returnListing}
                        handleUpdateQuantity={handleUpdateQuantity}
                        handleToggleSelect={handleToggleSelect}
                    />

                    {returnListing.length > 0 && !returnListing.some(item => item.isSelected) && (
                        <div className="text-center text-danger mt-3 fw-bold">
                            ⚠️ Please select at least one item from the list above to proceed with the return.
                        </div>
                    )}

                    {returnListing.length > 0 && (
                        <div className={classNames(styles.ButtonContainer, 'd-flex justify-content-end mt-3')}>
                            <CustomButton
                                text={'Submit Return'}
                                containerStyle={styles.SubmitBtn}
                                submitHandle={handleSubmit}
                            />
                        </div>
                    )}
                </div>
                <ReturnOrderModal
                    show={showModal}
                    handleClose={() => setShowModal(false)}
                    onSubmit={handleFinalSubmit}
                />
            </div>
            {returnLoading && <OverlayLoading />}
        </DashboardWrapper>
    );
}
